/**
 * The route sheet. It does two jobs now that we keep several routes at once.
 *
 * First, a list of EVERY route: tap a route's name to select it (make it the
 * active one), use the eye switch to show or hide it on the chart, and tap
 * Follow to navigate that specific route. So we can hold a handful of routes,
 * choose which are drawn, and pick any one to follow.
 *
 * Second, the detail for the selected route: in Plotting it's the BUILDER (drop
 * waypoints on the chart, tap a leg for its cross-track corridor, finish, edit).
 * Following always hands to Live, the same one-tap hand-off as "Follow in Live".
 *
 * Positions and bearings follow the same degrees, minutes and decimal-minutes
 * convention as the rest of the app.
 */
import { useEffect, useRef, useState } from 'react';
import { useChartPlotterStore } from '@/store';
import { useShellStore } from '@/store/shell';
import { useRoutingStore, selectActiveRoute } from '@/store/routing';
import { useSettingsStore } from '@/store/settings';
import { useEnvStore } from '@/store/environment';
import { haptic } from '@/ui/haptics';
import {
  summariseRoute,
  effectiveLegXtd,
  checkRouteSafety,
  summariseRouteSafety,
  gpxToRoutes,
  sweepDepartures,
  type Waypoint,
  type DepartureSweep,
} from '@/routing';
import { getChartProbe } from '@/map/chartProbe';
import { useRouteSafetyStore } from '@/store/routeSafety';
import { formatBearing, formatDistance } from '@/nav';
import { Sheet } from './Sheet';
import { EmptyHint } from './EmptyHint';
import { EMPTY_ROUTES } from './emptyHints';
import { GlossaryTerm } from '@/help/GlossaryTerm';
import { routeTimedSummary, formatTtg } from './routeTiming';
import { shareRoute } from './routeShare';

/**
 * A handful of clear, well-spaced colours for telling routes apart on the chart.
 * Chosen to read against both day and dusk chart backgrounds, and kept off the
 * magenta the active-leg course line uses so a followed leg still stands out.
 */
const ROUTE_COLOURS = [
  '#e53935', // red
  '#fb8c00', // orange
  '#fdd835', // amber
  '#43a047', // green
  '#00acc1', // teal
  '#1e88e5', // blue
  '#8e24aa', // violet
  '#6d4c41', // brown
] as const;

/** A short created date for a route row, e.g. "23 Jul 2026", or '' if unknown
 *  (routes saved by an older build carry no createdAt). */
function formatCreated(ms: number | undefined): string {
  if (!ms || !Number.isFinite(ms)) return '';
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

/** A departure instant as a local clock time, e.g. "14:20". If it falls on a
 *  different day to now, we prefix a short weekday ("Tue 06:40") so a tomorrow-
 *  morning gate doesn't read as if it were today. Local timezone, like the rest
 *  of the app's dates. */
function formatDeparture(ms: number, nowMs: number): string {
  try {
    const d = new Date(ms);
    const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    const sameDay = new Date(nowMs).toDateString() === d.toDateString();
    if (sameDay) return time;
    const day = d.toLocaleDateString(undefined, { weekday: 'short' });
    return `${day} ${time}`;
  } catch {
    return '';
  }
}

/** A saving in hours as a short human span, e.g. "40 min" or "1h 20m". We round
 *  to whole minutes because a tidal-stream estimate is nowhere near second-sharp. */
function formatSaving(hours: number): string {
  const mins = Math.round(hours * 60);
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

/**
 * One row of the route's waypoint editor: rename the waypoint, and arm or silence
 * its arrival alarm with a radius. Local state keeps typing smooth and is re-seeded
 * only on the waypoint's identity (and its radius, so a ring drag on the chart shows
 * the new value), committing to the store as we go.
 */
function WaypointEditorRow({
  routeId,
  wp,
  index,
  updateWaypoint,
}: {
  routeId: string;
  wp: Waypoint;
  index: number;
  updateWaypoint: (
    routeId: string,
    wpId: string,
    patch: Partial<Pick<Waypoint, 'name' | 'arrivalRadiusNm' | 'arrivalAlarm'>>,
  ) => void;
}) {
  const armed = wp.arrivalAlarm ?? typeof wp.arrivalRadiusNm === 'number';
  const [name, setName] = useState(wp.name ?? '');
  const [alarmOn, setAlarmOn] = useState(armed);
  const [radius, setRadius] = useState(
    wp.arrivalRadiusNm != null ? String(Number(wp.arrivalRadiusNm.toFixed(3))) : '0.10',
  );
  const wpRadius = wp.arrivalRadiusNm;
  useEffect(() => {
    setName(wp.name ?? '');
    setAlarmOn(wp.arrivalAlarm ?? typeof wp.arrivalRadiusNm === 'number');
    setRadius(wp.arrivalRadiusNm != null ? String(Number(wp.arrivalRadiusNm.toFixed(3))) : '0.10');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wp.id, wpRadius]);

  function commitAlarm(on: boolean, radStr: string) {
    const rad = parseFloat(radStr);
    updateWaypoint(routeId, wp.id, {
      arrivalAlarm: on,
      arrivalRadiusNm: on && Number.isFinite(rad) && rad > 0 ? rad : undefined,
    });
  }

  return (
    <div className="acp-wp-edit">
      <input
        className="acp-text-input"
        value={name}
        onChange={(e) => {
          setName(e.target.value);
          updateWaypoint(routeId, wp.id, { name: e.target.value });
        }}
        placeholder={`WP${index + 1}`}
        aria-label={`Waypoint ${index + 1} name`}
      />
      <div className="acp-seg">
        <button
          type="button"
          className={!alarmOn ? 'on' : ''}
          onClick={() => {
            setAlarmOn(false);
            commitAlarm(false, radius);
          }}
        >
          No alarm
        </button>
        <button
          type="button"
          className={alarmOn ? 'on' : ''}
          onClick={() => {
            setAlarmOn(true);
            commitAlarm(true, radius);
          }}
        >
          Arrival
        </button>
      </div>
      {alarmOn && (
        <input
          className="acp-text-input"
          value={radius}
          inputMode="decimal"
          onChange={(e) => {
            setRadius(e.target.value);
            commitAlarm(true, e.target.value);
          }}
          placeholder="0.10"
          aria-label={`Waypoint ${index + 1} arrival radius in nautical miles`}
        />
      )}
    </div>
  );
}

export function RouteSheet() {
  const routes = useRoutingStore((s) => s.routes);
  const activeRouteId = useRoutingStore((s) => s.activeRouteId);
  const setActiveRoute = useRoutingStore((s) => s.setActiveRoute);
  const renameRoute = useRoutingStore((s) => s.renameRoute);
  const updateRoute = useRoutingStore((s) => s.updateRoute);
  const updateWaypoint = useRoutingStore((s) => s.updateWaypoint);
  const route = useRoutingStore(selectActiveRoute);
  const hiddenRouteIds = useRoutingStore((s) => s.hiddenRouteIds);
  const toggleRouteHidden = useRoutingStore((s) => s.toggleRouteHidden);
  const removeRoute = useRoutingStore((s) => s.removeRoute);
  const reverseRoute = useRoutingStore((s) => s.reverseRoute);
  const duplicateRoute = useRoutingStore((s) => s.duplicateRoute);
  const importRoutes = useRoutingStore((s) => s.importRoutes);
  const buildingRouteId = useRoutingStore((s) => s.buildingRouteId);
  const finishRoute = useRoutingStore((s) => s.finishRoute);
  const setBuildingRoute = useRoutingStore((s) => s.setBuildingRoute);
  const setAwaitingRouteStart = useRoutingStore((s) => s.setAwaitingRouteStart);
  const followRouteId = useRoutingStore((s) => s.followRouteId);
  const activateRoute = useRoutingStore((s) => s.activateRoute);
  const deactivateRoute = useRoutingStore((s) => s.deactivateRoute);
  const mode = useShellStore((s) => s.mode);
  const setMode = useShellStore((s) => s.setMode);
  const openSheet = useShellStore((s) => s.openSheet);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const showToast = useShellStore((s) => s.showToast);
  const showConfirm = useShellStore((s) => s.showConfirm);
  const setEditingLegXtd = useShellStore((s) => s.setEditingLegXtd);
  const dataFields = useShellStore((s) => s.dataFields);
  const setDataFieldAt = useShellStore((s) => s.setDataFieldAt);
  const ownshipSog = useChartPlotterStore((s) => s.ownship.sog);
  const planSpeedRef = useSettingsStore((s) => s.planSpeedRef);
  const cruiseSpeedKn = useSettingsStore((s) => s.cruiseSpeedKn);
  const safetyDepthM = useSettingsStore((s) => s.safetyDepthM);
  const streamTimeMs = useEnvStore((s) => s.streamTimeMs);
  const setSettings = useSettingsStore((s) => s.set);
  const routeCheck = useRouteSafetyStore((s) => s.check);
  const setRouteCheck = useRouteSafetyStore((s) => s.setCheck);
  const clearRouteCheck = useRouteSafetyStore((s) => s.clear);

  // Best-departure (tidal gate) result, held in local state rather than a store —
  // it's a one-off answer for the panel, and keeping it out of zustand avoids the
  // new-object-selector re-render trap. nowMs is frozen alongside the result so the
  // clock labels stay put after the sweep runs.
  const [gate, setGate] = useState<{ result: DepartureSweep; nowMs: number } | null>(null);

  const planning = mode === 'Plotting';
  const building = buildingRouteId !== null;
  const following = !!route && followRouteId === route.id;
  const canFollow = !!route && route.waypoints.length >= 2;

  // Estimated timing uses the skipper's expected-speed setting (SOG or STW), the
  // same basis the build HUD and the Live data fields use, so the estimate here
  // matches what they'll read underway. STW folds the tidal stream into each leg;
  // SOG uses the live over-ground speed when moving and the cruising speed when
  // stopped. Editing the speed below writes straight to those shared settings.
  const summary = route
    ? routeTimedSummary(route, { planSpeedRef, cruiseSpeedKn, sog: ownshipSog })
    : null;
  const ttg = summary ? formatTtg(summary.totalHours) : null;
  // The speed the estimate is actually on, for the label.
  const shownSpeed =
    planSpeedRef === 'stw' ? cruiseSpeedKn : ownshipSog > 0 ? ownshipSog : cruiseSpeedKn;

  // Editable target speed for the estimate. Local state keeps typing smooth and
  // is re-seeded when the selected route changes (so reopening shows the current
  // setting), committing to the shared boat settings as we go.
  const [speedVal, setSpeedVal] = useState(cruiseSpeedKn != null ? String(cruiseSpeedKn) : '5');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setSpeedVal(cruiseSpeedKn != null ? String(cruiseSpeedKn) : '5'), [route?.id]);

  // Editable name for the selected route. Local state so typing is smooth;
  // re-seeded only when the selected route changes (not on its name) so live
  // edits aren't clobbered. Commits straight to the store, so it persists with
  // the rest of the route data and the list row updates as we type.
  const [name, setName] = useState(route?.name ?? '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setName(route?.name ?? ''), [route?.id]);

  // Free-text note on the route. Same local-state pattern as the name: smooth to
  // type, re-seeded only when the selected route changes, committed to the store
  // as we go so it persists with the rest of the route data.
  const [notes, setNotes] = useState(route?.notes ?? '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setNotes(route?.notes ?? ''), [route?.id]);

  // Overall cross-track limit for the route, surfaced inline so it sits with the
  // rest of the editor rather than only behind the Route alarms sheet. Per-leg
  // overrides are still set by tapping a leg below. Same smooth-typing pattern.
  const [xtdOn, setXtdOn] = useState(typeof route?.xtdNm === 'number');
  const [xtdVal, setXtdVal] = useState(route?.xtdNm != null ? String(route.xtdNm) : '0.20');
  const routeXtd = route?.xtdNm;
  useEffect(() => {
    setXtdOn(typeof routeXtd === 'number');
    setXtdVal(routeXtd != null ? String(routeXtd) : '0.20');
  }, [route?.id, routeXtd]);
  // Route-wide arrival alarms: "All waypoints" sets a route default radius that
  // every waypoint inherits; "Per waypoint" clears it so only points armed
  // individually alarm. Per-waypoint switches always win either way.
  const [arrVal, setArrVal] = useState(
    route?.arrivalRadiusNm != null ? String(route.arrivalRadiusNm) : '0.10',
  );
  const routeArr = route?.arrivalRadiusNm;
  useEffect(() => {
    setArrVal(routeArr != null ? String(routeArr) : '0.10');
  }, [route?.id, routeArr]);

  // Make sure a route metric is visible once we start following.
  function ensureRouteField() {
    const routeFields = ['dtwp', 'brgwp', 'xte', 'eta'];
    if (!dataFields.some((f) => routeFields.includes(f))) setDataFieldAt(0, 'dtwp');
  }

  // Run the route safety check against the live chart. It reuses the same land
  // polygons and charted-depth sampler the map already has loaded, so a "Check
  // route" reads exactly what's on the chart in front of us. The result goes into
  // its own store so both this panel and the chart highlight stay in step.
  function checkRoute() {
    if (!route || route.waypoints.length < 2) return;
    const probe = getChartProbe();
    if (!probe) {
      showToast('Charts still loading, try Check route again in a moment');
      return;
    }
    const result = checkRouteSafety({
      waypoints: route.waypoints.map((w) => w.position),
      safetyDepthM,
      landRingsForLeg: probe.landRingsForLeg,
      depthAt: probe.depthAt,
      inCoverage: probe.inCoverage,
      // Tide on top of the charted depth: a shallow leg may open a tidal gate, be a
      // genuine hazard even at high water, or need a manual calc where there's no
      // height data. The window starts at the scrubber time (the tide model's own
      // clock) and looks a tidal day ahead.
      tideHeightAt: probe.tideHeightAt,
      assessFromMs: streamTimeMs,
      assessWindowHours: 24,
    });
    setRouteCheck(route.id, result);
    showToast(summariseRouteSafety(result).headline);
  }

  // Best departure (tidal gate): sweep the next 24 h for the departure that
  // carries the most fair tide, timing the whole route from each candidate. It
  // reuses the same tide-folded STW timing the estimate above uses, and the same
  // chart-diamond stream source, so it agrees with the rest of the app. Runs on
  // the tap (bounded evaluations), reads "now" once, and drops the answer into
  // local state for the panel below.
  function runBestDeparture() {
    if (!route || route.waypoints.length < 2) return;
    const nowMs = Date.now();
    const result = sweepDepartures(
      route,
      { ref: planSpeedRef === 'stw' ? 'stw' : 'sog', knots: cruiseSpeedKn },
      { startMs: nowMs, windowHours: 24, stepMinutes: 30 },
    );
    setGate({ result, nowMs });
  }
  // Clear the gate answer when the selected route changes — a result for one route
  // shouldn't linger on another.
  useEffect(() => setGate(null), [route?.id]);

  // The check panel only shows while its result belongs to the selected route.
  const activeCheck = route && routeCheck && routeCheck.routeId === route.id ? routeCheck : null;
  const checkSummary = activeCheck ? summariseRouteSafety(activeCheck.result) : null;

  // Prepared view data for the best-departure mini bar: a handful of evenly-spaced
  // candidates with a bar length scaled by passage time (longer bar = slower), and
  // the best one flagged so it stands out.
  const gateOk = gate && gate.result.status === 'ok' ? gate.result : null;
  const gateBars = (() => {
    if (!gateOk) return [] as { departMs: number; pct: number; isBest: boolean; foul: boolean }[];
    const cs = gateOk.candidates;
    const totals = cs.map((c) => c.totalHours).filter((h) => Number.isFinite(h));
    const min = totals.length ? Math.min(...totals) : 0;
    const max = totals.length ? Math.max(...totals) : 1;
    const span = max - min || 1;
    const stride = Math.max(1, Math.ceil(cs.length / 8));
    return cs
      .filter((_, i) => i % stride === 0)
      .map((c) => ({
        departMs: c.departMs,
        pct: Number.isFinite(c.totalHours) ? 8 + 92 * ((c.totalHours - min) / span) : 100,
        isBest: c.departMs === gateOk.best.departMs,
        foul: c.anyUnachievable,
      }));
  })();

  function selectRoute(id: string) {
    setActiveRoute(id);
    const r = routes.find((x) => x.id === id);
    showToast(`Selected ${r?.name ?? 'route'}`);
  }

  // Activate a chosen route: put it into follow mode from leg 1 and hand to Live,
  // the same hand-off as the "Go" button on a freshly built route.
  function followFromList(id: string) {
    setActiveRoute(id);
    setMode('Live');
    haptic('commit');
    activateRoute(id);
    ensureRouteField();
    closeSheet();
    showToast('Route activated');
  }

  function finish() {
    haptic('commit');
    finishRoute();
    showToast('Route finished');
  }
  function addMore() {
    if (route) setBuildingRoute(route.id);
    closeSheet();
    showToast('Long-press the chart to add waypoints');
  }
  function discardBuilding() {
    if (buildingRouteId) removeRoute(buildingRouteId);
    showToast('Route discarded');
  }
  // Delete a route by id, always behind an are-you-sure so a stray tap can't nuke
  // it (a route plus all its waypoints is a lot to lose). Shared by the list-row
  // ✕ and the selected-route Delete button so both ask the same way.
  function deleteRoute(id: string, name: string) {
    showConfirm({
      message: `Delete “${name}”? This removes the route and all its waypoints.`,
      confirmLabel: 'Delete route',
      onConfirm: () => {
        removeRoute(id);
        showToast('Route deleted');
      },
    });
  }
  function deleteActive() {
    if (!route) return;
    deleteRoute(route.id, route.name);
  }
  async function exportActive() {
    if (!route) return;
    try {
      const how = await shareRoute(route);
      showToast(how === 'shared' ? 'Route shared as GPX' : 'Route saved as GPX');
    } catch (e) {
      // A user cancelling the native share sheet isn't an error worth flagging.
      if ((e as { name?: string })?.name !== 'AbortError') showToast('Could not export the route');
    }
  }

  // Hidden file input, triggered by the Import GPX buttons. One input serves every
  // trigger; clearing its value after a read lets the same file be picked twice.
  const fileInputRef = useRef<HTMLInputElement>(null);
  function openImportPicker() {
    fileInputRef.current?.click();
  }
  function onImportFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onerror = () => showToast('Could not read that file');
    reader.onload = () => {
      try {
        const { routes: parsed, skippedPoints, trackPointsIgnored } = gpxToRoutes(
          String(reader.result ?? ''),
        );
        if (parsed.length === 0) {
          // A readable file that held no route or waypoint we could use (e.g. a
          // track-only export). Say what we saw rather than a bare "nothing".
          showToast(
            trackPointsIgnored > 0
              ? `No routes found — ignored a track of ${trackPointsIgnored} points`
              : 'No routes or waypoints found in that file',
          );
          return;
        }
        const added = importRoutes(parsed);
        const first = added[0];
        const wpLabel = `${first.waypoints.length} ${
          first.waypoints.length === 1 ? 'waypoint' : 'waypoints'
        }`;
        const extra =
          (added.length > 1 ? ` (+${added.length - 1} more)` : '') +
          (skippedPoints > 0 ? `, skipped ${skippedPoints} bad point${skippedPoints === 1 ? '' : 's'}` : '');
        showToast(`Imported ${first.name}, ${wpLabel}${extra}`);
      } catch (err) {
        // A bad or non-GPX file reports cleanly rather than crashing the sheet.
        showToast((err as Error)?.message ?? 'Could not import that GPX file');
      }
    };
    reader.readAsText(file);
  }
  function reverseActive() {
    if (!route) return;
    reverseRoute(route.id);
    showToast('Route reversed');
  }
  function duplicateActive() {
    if (!route) return;
    // duplicateRoute selects the copy, so the sheet immediately shows the new
    // route ready to edit as a variation, leaving the original as it was.
    duplicateRoute(route.id);
    showToast('Route duplicated');
  }
  function newRoute() {
    // Non-destructive: leave existing routes alone, just stop building any
    // current one. Arm "awaiting first point" so the very next long-press drops
    // the first waypoint and starts the route, rather than opening the open-water
    // radial ring, which is what used to hijack this gesture. Building happens in
    // Plotting, so if we're in Live we switch across first — otherwise the long-
    // press wouldn't start the route.
    if (!planning) setMode('Plotting');
    finishRoute();
    setActiveRoute(null);
    setAwaitingRouteStart(true);
    closeSheet();
    showToast('Long-press the chart to drop the first waypoint');
  }
  function follow() {
    if (!route) return;
    haptic('commit');
    activateRoute(route.id);
    ensureRouteField();
    closeSheet();
    showToast('Route activated');
  }
  function followInLive() {
    if (!route) return;
    setMode('Live');
    haptic('commit');
    activateRoute(route.id);
    ensureRouteField();
    closeSheet();
    showToast('Route activated');
  }
  function stop() {
    haptic('commit');
    deactivateRoute();
    showToast('Stopped following');
  }

  const sub = planning
    ? 'Your routes are listed below. Tap one to select it, use the eye to show or hide it on the chart, or tap Follow to navigate it. Select a route to build or edit it underneath.'
    : 'The routes you built in Plotting are here. Tap Follow on any one to navigate it; the data fields read live as we move.';

  return (
    <Sheet name="route" title="Routes" sub={sub}>
      {/* One hidden file input drives every Import GPX button below. */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".gpx,application/gpx+xml,text/xml,application/xml"
        hidden
        onChange={onImportFile}
      />
      {/* The list of every route. Hidden while actively building, so the builder
          has the floor. */}
      {routes.length > 0 && !building && (
        <div className="acp-route-list">
          {routes.map((r) => {
            const active = r.id === activeRouteId;
            const hidden = hiddenRouteIds.includes(r.id);
            const isFollowing = followRouteId === r.id;
            const sm = summariseRoute(r);
            const wpLabel = `${r.waypoints.length} ${r.waypoints.length === 1 ? 'waypoint' : 'waypoints'}`;
            const created = formatCreated(r.createdAt);
            const meta =
              (r.waypoints.length >= 2 ? `${wpLabel} · ${formatDistance(sm.totalNm)}` : wpLabel) +
              (created ? ` · ${created}` : '') +
              (isFollowing ? ' · following' : hidden ? ' · hidden' : '');
            return (
              <div className={`acp-route-row${active ? ' active' : ''}`} key={r.id}>
                <button type="button" className="acp-route-sel" onClick={() => selectRoute(r.id)}>
                  <b>
                    {r.name}
                    {active ? '  ✓' : ''}
                  </b>
                  <span>{meta}</span>
                </button>
                <button
                  type="button"
                  className={`acp-sw${hidden ? '' : ' on'}`}
                  aria-pressed={!hidden}
                  aria-label={`${hidden ? 'Show' : 'Hide'} ${r.name} on the chart`}
                  title={hidden ? 'Hidden — tap to show on the chart' : 'Shown — tap to hide'}
                  onClick={() => toggleRouteHidden(r.id)}
                />
                <button
                  type="button"
                  className="acp-btn acp-route-foll"
                  disabled={r.waypoints.length < 2}
                  onClick={() => followFromList(r.id)}
                >
                  {isFollowing ? 'Following' : 'Follow'}
                </button>
                <button
                  type="button"
                  className="acp-route-del"
                  aria-label={`Delete ${r.name}`}
                  title="Delete route"
                  onClick={() => deleteRoute(r.id, r.name)}
                >
                  ✕
                </button>
              </div>
            );
          })}
          <div className="acp-btn-row">
            <button type="button" className="acp-btn" onClick={newRoute}>
              New route
            </button>
            <button type="button" className="acp-btn" onClick={openImportPicker}>
              Import GPX
            </button>
          </div>
        </div>
      )}

      {/* Nothing saved yet: a friendly teaching line instead of a blank, with the
          same New route / Import buttons so it's still a place to start from. */}
      {routes.length === 0 && !building && (
        <div className="acp-route-list">
          <EmptyHint>{EMPTY_ROUTES}</EmptyHint>
          <div className="acp-btn-row">
            <button type="button" className="acp-btn" onClick={newRoute}>
              New route
            </button>
            <button type="button" className="acp-btn" onClick={openImportPicker}>
              Import GPX
            </button>
          </div>
        </div>
      )}

      {/* Status note for the selected route. */}
      {building ? (
        <p className="acp-buildnote">
          ● Building this route. Long-press the chart to add waypoints, then tap Finish.
        </p>
      ) : following ? (
        <p className="acp-buildnote">
          ▸ Following this route. The active leg is drawn to the next waypoint, and the data field
          reads live as we move.
        </p>
      ) : route && route.waypoints.length >= 2 ? (
        <p className="acp-buildnote done">
          {planning
            ? 'Tap a waypoint to edit it or drag it on the chart, or tap a leg below to set its corridor.'
            : 'Tap Follow to start navigating this route.'}
        </p>
      ) : null}

      {/* Edit the selected route: name, notes, overall XTD, and per-waypoint
          names and arrival alarms. Shown whenever a route is selected, in any
          mode. Editing commits straight to the store, so it's just as safe in
          Live as in Plotting, and the Routes sheet is usually opened from Live.
          Previously this was gated behind Plotting only, so opening Routes in
          Live gave no way to rename the route or its waypoints — which is what
          fb-ms1hbrgv-3, fb-ms1ij3qg-1 and fb-ms1nnlh9-2 were all reporting. */}
      {route && (
        <>
          <label className="acp-fieldlabel">Route name</label>
          <input
            className="acp-text-input"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              renameRoute(route.id, e.target.value);
            }}
            placeholder="Route name"
            aria-label="Route name"
          />
          <label className="acp-fieldlabel">Route colour</label>
          <div className="acp-swatches">
            <button
              type="button"
              className={`acp-sw-def${!route.color ? ' on' : ''}`}
              onClick={() => updateRoute(route.id, { color: undefined })}
              title="Use the default route colour"
            >
              Default
            </button>
            {ROUTE_COLOURS.map((c) => (
              <button
                key={c}
                type="button"
                className={`acp-sw${route.color === c ? ' on' : ''}`}
                style={{ background: c }}
                onClick={() => updateRoute(route.id, { color: c })}
                aria-label={`Set route colour ${c}`}
                aria-pressed={route.color === c}
                title={c}
              />
            ))}
          </div>
          <label className="acp-fieldlabel">Notes</label>
          <textarea
            className="acp-note-input"
            value={notes}
            rows={3}
            onChange={(e) => {
              setNotes(e.target.value);
              updateRoute(route.id, { notes: e.target.value });
            }}
            placeholder="Notes for this route — pilotage reminders, hazards, tidal gates"
            aria-label="Route notes"
          />
          <label className="acp-fieldlabel">Arrival alarms</label>
          <div className="acp-seg">
            <button
              type="button"
              className={typeof routeArr === 'number' ? 'on' : ''}
              onClick={() => {
                const v = parseFloat(arrVal);
                updateRoute(route.id, {
                  arrivalRadiusNm: Number.isFinite(v) && v > 0 ? v : 0.1,
                });
              }}
            >
              All waypoints
            </button>
            <button
              type="button"
              className={typeof routeArr !== 'number' ? 'on' : ''}
              onClick={() => updateRoute(route.id, { arrivalRadiusNm: undefined })}
            >
              Per waypoint
            </button>
          </div>
          {typeof routeArr === 'number' && (
            <input
              className="acp-text-input"
              value={arrVal}
              inputMode="decimal"
              onChange={(e) => {
                setArrVal(e.target.value);
                const v = parseFloat(e.target.value);
                if (Number.isFinite(v) && v > 0) updateRoute(route.id, { arrivalRadiusNm: v });
              }}
              placeholder="0.10"
              aria-label="Arrival radius for every waypoint, nautical miles"
            />
          )}
          <p className="acp-menu-note">
            All waypoints arms an arrival ring on every point at this radius; a waypoint switched off in
            its own editor stays off. Per waypoint alarms only the points armed individually. New routes
            start with all waypoints on, because arming twelve points one by one is how one gets missed.
          </p>
          <label className="acp-fieldlabel">Overall cross-track limit (<GlossaryTerm termKey="xtd">XTD</GlossaryTerm>)</label>
          <div className="acp-seg">
            <button
              type="button"
              className={!xtdOn ? 'on' : ''}
              onClick={() => {
                setXtdOn(false);
                updateRoute(route.id, { xtdNm: undefined });
              }}
            >
              Off
            </button>
            <button
              type="button"
              className={xtdOn ? 'on' : ''}
              onClick={() => {
                setXtdOn(true);
                const v = parseFloat(xtdVal);
                // On means on: with no readable figure in the field, take the
                // default rather than committing undefined and staying off.
                updateRoute(route.id, { xtdNm: Number.isFinite(v) && v > 0 ? v : 0.2 });
              }}
            >
              On
            </button>
          </div>
          {xtdOn && (
            <input
              className="acp-text-input"
              value={xtdVal}
              inputMode="decimal"
              onChange={(e) => {
                setXtdVal(e.target.value);
                const v = parseFloat(e.target.value);
                // Only a valid figure commits. Clearing the field to type a new
                // number left it briefly empty, and committing that emptiness
                // switched the corridor OFF under the skipper's fingers
                // (fb-msnbdw7w-0). An empty field is someone mid-thought, not
                // an instruction; Off is the Off button's job.
                if (Number.isFinite(v) && v > 0) updateRoute(route.id, { xtdNm: v });
              }}
              placeholder="0.20"
              aria-label="Overall cross-track limit in nautical miles"
            />
          )}
          <label className="acp-fieldlabel">Estimated speed (for time-to-go)</label>
          <div className="acp-seg">
            <button
              type="button"
              className={planSpeedRef === 'sog' ? 'on' : ''}
              onClick={() => setSettings({ planSpeedRef: 'sog' })}
            >
              Over ground (SOG)
            </button>
            <button
              type="button"
              className={planSpeedRef === 'stw' ? 'on' : ''}
              onClick={() => setSettings({ planSpeedRef: 'stw' })}
            >
              Through water (STW)
            </button>
          </div>
          <input
            className="acp-text-input"
            value={speedVal}
            inputMode="decimal"
            onChange={(e) => {
              setSpeedVal(e.target.value);
              const v = parseFloat(e.target.value);
              if (Number.isFinite(v) && v > 0) setSettings({ cruiseSpeedKn: v });
            }}
            placeholder="5"
            aria-label="Estimated speed in knots"
          />
          <p className="acp-subnote">
            SOG is already the speed over the ground, so the estimate is distance ÷ speed. STW is the
            speed through the water, so we fold the tidal stream into each leg to get the real time on
            the ground.
          </p>
          {route.waypoints.length > 0 && (
            <>
              <label className="acp-fieldlabel">Waypoints</label>
              <p className="acp-subnote">
                Rename each waypoint, and arm its arrival alarm with a radius. Off keeps the point
                but sounds nothing there.
              </p>
              {route.waypoints.map((w, i) => (
                <WaypointEditorRow
                  key={w.id}
                  routeId={route.id}
                  wp={w}
                  index={i}
                  updateWaypoint={updateWaypoint}
                />
              ))}
            </>
          )}
        </>
      )}

      {/* Selected route detail, or a prompt when there's nothing to show. */}
      {!route || route.waypoints.length < 2 ? (
        <p className="acp-empty">
          {planning
            ? routes.length === 0
              ? 'No routes yet. Long-press the chart to drop waypoints, or tap a point and choose “Start route here”. Build two or more and the legs and totals show up here.'
              : 'Select a route above to see its legs, or long-press the chart to keep building this one.'
            : 'No route to follow yet. Switch to Plotting to build one, then come back here to follow it.'}
        </p>
      ) : (
        <>
          {summary!.legs.map((leg, i) => {
            const from = route.waypoints[i];
            const to = route.waypoints[i + 1];
            const mins = leg.etaHours !== undefined ? Math.round(leg.etaHours * 60) : null;
            const xtd = effectiveLegXtd(route, leg.fromId);
            const legText = (
              <>
                <span className="ln">
                  {from.name} to {to.name}
                </span>
                <span className="ld">
                  {formatDistance(leg.distanceNm)} · {formatBearing(leg.bearingDeg)}
                  {mins !== null ? ` · ${mins} min` : ''}
                  {xtd != null ? ` · XTD ${xtd.toFixed(2)}` : ''}
                </span>
              </>
            );
            return planning ? (
              <button
                type="button"
                className="acp-leg acp-leg-btn"
                key={`${leg.fromId}-${leg.toId}`}
                onClick={() => {
                  setEditingLegXtd({ routeId: route.id, fromId: leg.fromId });
                  openSheet('legxtd');
                }}
              >
                {legText}
              </button>
            ) : (
              <div className="acp-leg" key={`${leg.fromId}-${leg.toId}`}>
                {legText}
              </div>
            );
          })}
          <div className="acp-kv" style={{ marginTop: 8 }}>
            <span className="k">Total distance</span>
            <span className="v">{formatDistance(summary!.totalNm)}</span>
          </div>
          <div className="acp-kv">
            <span className="k">
              Estimated time{summary!.basis === 'stw-tide' ? ' (tide)' : ''}
            </span>
            <span className="v">
              {ttg
                ? `${ttg} at ${shownSpeed.toFixed(1)} kn ${planSpeedRef.toUpperCase()}`
                : 'Set a speed'}
              {summary!.anyUnachievable ? ' !' : ''}
            </span>
          </div>
        </>
      )}

      {/* Route safety check: run the land/shallow check over the whole route and
          show a plain summary. Highlighted legs appear on the chart too. Available
          whenever a route has legs, in any mode. */}
      {route && route.waypoints.length >= 2 && (
        <div className="acp-btn-row">
          <button type="button" className="acp-btn full" onClick={checkRoute}>
            Check route
          </button>
        </div>
      )}
      {checkSummary && (
        <div className="acp-buildnote done acp-route-check">
          <b>{checkSummary.headline}</b>
          {checkSummary.hazards.map((line, i) => (
            <p key={`h${i}`} className="acp-subnote acp-route-check-hazard">
              ● {line}
            </p>
          ))}
          {checkSummary.gates.map((line, i) => (
            <p key={`g${i}`} className="acp-subnote acp-route-check-gate">
              ◈ {line}
            </p>
          ))}
          {checkSummary.manualTide.map((line, i) => (
            <p key={`m${i}`} className="acp-subnote acp-route-check-manual">
              ◆ {line}
            </p>
          ))}
          {checkSummary.cautions.map((line, i) => (
            <p key={`c${i}`} className="acp-subnote acp-route-check-caution">
              ▲ {line}
            </p>
          ))}
          {checkSummary.safetyNote && <p className="acp-subnote">{checkSummary.safetyNote}</p>}
          <p className="acp-subnote">
            This checks your route against the charts we have loaded at the current detail. It&apos;s
            an aid, not a guarantee, so keep your own lookout and cross-check the paper chart and
            almanac.
          </p>
          <button type="button" className="acp-btn" onClick={clearRouteCheck}>
            Clear check
          </button>
        </div>
      )}

      {/* Best departure (tidal gate): find the time to leave in the next 24 h that
          carries the most fair tide. Available whenever a route has legs, in any
          mode, same as the safety check. */}
      {route && route.waypoints.length >= 2 && (
        <div className="acp-btn-row">
          <button type="button" className="acp-btn full" onClick={runBestDeparture}>
            Best departure
          </button>
        </div>
      )}
      {gate && gate.result.status === 'needs-stw' && (
        <div className="acp-buildnote done acp-route-gate">
          <b>Set a through-water speed to optimise for tide</b>
          <p className="acp-subnote">
            The estimate is on speed over the ground (SOG), which already has the tide in it, so
            every departure works out the same and there&apos;s nothing to time. Switch the estimated
            speed to Through water (STW) above and give us the boat&apos;s speed through the water,
            then we can find the fair-tide window.
          </p>
          <button type="button" className="acp-btn" onClick={() => setGate(null)}>
            Dismiss
          </button>
        </div>
      )}
      {gate && gate.result.status === 'no-stream-data' && (
        <div className="acp-buildnote done acp-route-gate">
          <b>No tidal data for this route</b>
          <p className="acp-subnote">
            We&apos;ve no tidal-stream information over this route, so there&apos;s no fair or foul
            tide to time against. Nothing to optimise here rather than a made-up pick.
          </p>
          <button type="button" className="acp-btn" onClick={() => setGate(null)}>
            Dismiss
          </button>
        </div>
      )}
      {gateOk && (
        <div className="acp-buildnote done acp-route-gate">
          {gateOk.allUnachievable ? (
            <>
              <b>The tide beats the boat across the whole window</b>
              <p className="acp-subnote">
                At {cruiseSpeedKn.toFixed(1)} kn through the water, the stream stops us on part of
                this route at every departure over the next {gateOk.windowHours} h. The least-bad
                start is{' '}
                <b>{formatDeparture(gateOk.best.departMs, gate!.nowMs)}</b>, but we&apos;d really want
                more speed or a different route.
              </p>
            </>
          ) : gateOk.negligible ? (
            <>
              <b>Leave {formatDeparture(gateOk.best.departMs, gate!.nowMs)}</b>
              <p className="acp-subnote">
                Passage about {formatTtg(gateOk.best.totalHours)}. The tide barely changes the time
                over the next {gateOk.windowHours} h on this route, so the departure isn&apos;t
                critical, leave when it suits.
              </p>
            </>
          ) : (
            <>
              <b>
                Leave {formatDeparture(gateOk.best.departMs, gate!.nowMs)} —{' '}
                {formatTtg(gateOk.best.totalHours)} passage
              </b>
              <p className="acp-subnote">
                About {formatSaving(gateOk.savingHours)} faster than the worst slot in the window
                (leaving {formatDeparture(gateOk.worst.departMs, gate!.nowMs)},{' '}
                {formatTtg(gateOk.worst.totalHours)}).
              </p>
            </>
          )}
          {gateBars.length > 1 && (
            <div className="acp-gate-bars" role="img" aria-label="Passage time by departure time">
              {gateBars.map((b) => (
                <div className="acp-gate-bar-row" key={b.departMs}>
                  <span className="acp-gate-bar-time">
                    {formatDeparture(b.departMs, gate!.nowMs)}
                  </span>
                  <span className="acp-gate-bar-track">
                    <span
                      className={`acp-gate-bar-fill${b.isBest ? ' best' : ''}${b.foul ? ' foul' : ''}`}
                      style={{ width: `${b.pct}%` }}
                    />
                  </span>
                </div>
              ))}
              <p className="acp-subnote">Shorter bar is a quicker passage. The best time is marked.</p>
            </div>
          )}
          <p className="acp-subnote">
            This times your route against the predicted tidal streams over the charts and data we
            have. It&apos;s an aid, not a guarantee, so cross-check the almanac and your own tidal
            planning.
          </p>
          <button type="button" className="acp-btn" onClick={() => setGate(null)}>
            Clear
          </button>
        </div>
      )}

      {/* Actions for the selected route. */}
      {planning && route && !building && route.waypoints.length >= 1 && (
        <div className="acp-btn-row">
          <button type="button" className="acp-btn full" onClick={() => openSheet('routealarms')}>
            Route alarms
          </button>
        </div>
      )}

      {following ? (
        <div className="acp-btn-row">
          <button type="button" className="acp-btn primary" onClick={stop}>
            Stop following
          </button>
        </div>
      ) : planning ? (
        building ? (
          <div className="acp-btn-row">
            <button type="button" className="acp-btn" onClick={discardBuilding}>
              Discard
            </button>
            <button type="button" className="acp-btn primary" onClick={finish}>
              Finish route
            </button>
          </div>
        ) : route ? (
          <>
            <div className="acp-btn-row">
              <button type="button" className="acp-btn" onClick={addMore}>
                Add more
              </button>
              <button
                type="button"
                className="acp-btn"
                onClick={exportActive}
                disabled={route.waypoints.length === 0}
              >
                Export GPX
              </button>
            </div>
            <div className="acp-btn-row">
              {route.waypoints.length >= 2 && (
                <button type="button" className="acp-btn" onClick={reverseActive}>
                  Reverse
                </button>
              )}
              <button
                type="button"
                className={`acp-btn${route.waypoints.length >= 2 ? '' : ' full'}`}
                onClick={duplicateActive}
              >
                Duplicate
              </button>
            </div>
            <div className="acp-btn-row">
              <button type="button" className="acp-btn full" onClick={deleteActive}>
                Delete route
              </button>
            </div>
            {canFollow && (
              <div className="acp-btn-row">
                <button type="button" className="acp-btn primary full" onClick={followInLive}>
                  ▶ Go (activate route)
                </button>
              </div>
            )}
          </>
        ) : routes.length === 0 ? (
          <div className="acp-btn-row">
            <button type="button" className="acp-btn primary" onClick={newRoute}>
              New route
            </button>
            <button type="button" className="acp-btn" onClick={openImportPicker}>
              Import GPX
            </button>
          </div>
        ) : null
      ) : route ? (
        // Following hands out via the top actions; here in Live/World a selected
        // route still needs Follow (when it has legs) and, so a route tapped on
        // the chart can always be cleared away, its own Delete route with the
        // same are-you-sure confirm as the Plotting list.
        <>
          {canFollow && (
            <div className="acp-btn-row">
              <button type="button" className="acp-btn primary" onClick={follow}>
                ▶ Go (activate route)
              </button>
            </div>
          )}
          <div className="acp-btn-row">
            <button type="button" className="acp-btn full" onClick={deleteActive}>
              Delete route
            </button>
          </div>
        </>
      ) : null}
    </Sheet>
  );
}
